import { FC, useCallback } from 'react';
import { useContextSelector } from 'use-context-selector';

import { authSelector } from '@/core/store/global/app-states';
import { AppContext } from '@/core/store/global/AppProvider';
import { appSettings } from '@/settings/app-settings';
import { Button } from '@/ux/components/shadcn/ui/button';
import { LoadingCircleIcon } from '@/ux-legacy/uikit/iconset/iconset-index';

type ConfirmationProp = {
  data: any;
  stage: number;
  onBack: any;
  stages: any[];
  isSubmitInProgress: boolean;
  onSubmit: (data: any, options?: any) => void;
  checkoutType?: 'CART' | 'QUICK';
  paymentModes: { stripe: boolean; vipps: boolean; dpo: boolean };
  totalAmount: number;
  handleGoToTab?: (index: number) => void;
};

const mapFields: any = {
  participantData: 'Participant Info',
  addressData: 'Address',
  businessDetails: 'Business',
  userDetails: 'User Details',
};

const filterNonEmptyObjects = (obj: any) => {
  if (Array.isArray(obj)) {
    // Filter out nulls and empty objects from arrays
    const filteredArray: any = obj
      .map(filterNonEmptyObjects)
      .filter((item) => item !== null && !(typeof item === 'object' && Object.keys(item).length === 0));
    return filteredArray.length > 0 ? filteredArray : null; // Return null if array is empty
  } else if (typeof obj === 'object' && obj !== null) {
    // Filter object properties recursively
    const filteredEntries: any = Object.entries(obj)
      .map(([key, value]) => [key, filterNonEmptyObjects(value)])
      .filter(
        ([_, value]) =>
          value && value !== null && !(typeof value === 'object' && Object.keys(value).length === 0),
      );

    return filteredEntries.length > 0 ? Object.fromEntries(filteredEntries) : null; // Return null if object is empty
  }

  return obj; // Return primitive values as-is
};

const Confirmation: FC<ConfirmationProp> = ({
  data,
  stages,
  isSubmitInProgress,
  onSubmit,
  onBack,
  stage,
  checkoutType,
  paymentModes,
  totalAmount,
  handleGoToTab,
}) => {
  const formattedData = filterNonEmptyObjects(data);
  const authentication = useContextSelector(AppContext, authSelector);

  const submitForm = (mode?: any) => {
    const data = {
      ...formattedData,
      participantData: formattedData?.participantData?.flatMap((part: any) => part.participants || []),
    };
    onSubmit(data, mode);
  };

  const isTabExist = useCallback(
    (name: string) => {
      const index = stages?.findIndex((item: any) => item?.name == name);

      if (index == -1) {
        return false;
      }

      return true;
    },
    [stages],
  );

  const gotoTab = (name: string) => {
    const index = stages?.findIndex((item: any) => item?.name == name);

    if (index >= 0) {
      handleGoToTab?.(index);
    }
  };

  return (
    <div className="flex gap-2 flex-col h-full">
      <div className="grow overflow-y-auto">
        <div className="p-4">
          {isTabExist('User Details') && (
            <div>
              <div className="text-md font-semibold">User Details</div>
              <div className="pl-4">
                {data?.userDetails ? (
                  <div>
                    <>
                      <p>{data?.userDetails?.name}</p>
                      <p>
                        {data?.userDetails?.country?.dial_code}
                        {data?.userDetails?.phone}
                      </p>
                      <p>{data?.userDetails?.email}</p>
                    </>
                  </div>
                ) : (
                  <button
                    className="px-4 text-sm py-1 text-blue-400 underline"
                    onClick={() => gotoTab('User Details')}
                  >
                    Add User Details
                  </button>
                )}
              </div>
            </div>
          )}

          {isTabExist('Participant Info') && (
            <div>
              <div className="text-md font-semibold mt-2">Participants</div>
              {data?.participantData?.length > 0 && data?.participantData[0]?.participants[0]?.name ? (
                <>
                  {data?.participantData?.map((product: any, index: number) => (
                    <div key={index} className="pl-4">
                      {/* {data?.participantData?.length > 0 && <div className="font-semibold">{product?.name}</div>} */}
                      {product?.participants?.map((participant: any, participantIndex: number) => (
                        <div className="pl-1 mt-2" key={participantIndex}>
                          <p>{participant?.name}</p>
                          <p>{participant?.email}</p>
                          <p>{participant?.gender}</p>
                          <p>{participant?.monthAndYearOfBirth}</p>
                          <p>{participant?.allergies}</p>
                        </div>
                      ))}
                    </div>
                  ))}
                </>
              ) : (
                <button
                  className="px-4 text-sm py-1 text-blue-400 underline"
                  onClick={() => gotoTab('Participant Info')}
                >
                  Add Participants
                </button>
              )}
            </div>
          )}

          {isTabExist('Address') && (
            <div>
              <div className="text-md font-semibold mt-2">Address</div>

              <div className="pl-4">
                {data?.addressData && data?.addressData?.Line1 ? (
                  <>
                    <p>{data?.addressData?.Line1}</p>
                    <p>{data?.addressData?.Line2}</p>
                    <p>{data?.addressData?.City}</p>
                    <p>{data?.addressData?.State}</p>
                    <p>{data?.addressData?.Country}</p>
                    <p>{data?.addressData?.ZipCode}</p>
                  </>
                ) : (
                  <button
                    className="px-4 text-sm py-1 text-blue-400 underline"
                    onClick={() => gotoTab('Address')}
                  >
                    Add Address
                  </button>
                )}
              </div>
            </div>
          )}

          {isTabExist('Business') && (
            <div>
              <div className="text-md font-semibold mt-2">Business</div>
              <div className="pl-4">
                {data?.businessDetails && data?.businessDetails?.name ? (
                  <p>{data?.businessDetails?.name}</p>
                ) : (
                  <button
                    className="px-4 text-sm py-1 text-blue-400 underline"
                    onClick={() => gotoTab('Address')}
                  >
                    Add Business
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        {/* )} */}
      </div>
      <div className={`flex ${stage > 0 ? 'justify-between' : 'justify-end'} mt-4 p-2`}>
        {stage > 0 && (
          <>
            <Button onClick={onBack} className="bg-black text-white">
              Back
            </Button>
            {checkoutType == 'QUICK' && (
              <button
                className="text-white bg-black rounded-full flex gap-2 py-2 px-4"
                onClick={() => submitForm()}
              >
                {isSubmitInProgress && <LoadingCircleIcon classNames="h-4 w-4" />}
                Submit
              </button>
            )}
          </>
        )}
      </div>
      <div className="w-full flex flex-col gap-2">
        {checkoutType == 'CART' && (
          <div className="flex flex-col justify-center items-center gap-2 p-2">
            {isSubmitInProgress && <LoadingCircleIcon classNames="h-8 w-8 text-black " />}
            {totalAmount <= 0 && (
              <button
                className="text-white bg-black flex justify-center rounded-full flex gap-2 py-2 px-4 w-full"
                onClick={() => submitForm({ mode: 'FREE' })}
              >
                {isSubmitInProgress && <LoadingCircleIcon classNames="h-4 w-4" />}
                Submit
              </button>
            )}
            {totalAmount > 0 && paymentModes?.stripe && (
              <button
                id="stripeButton"
                className={`text-white w-full h-[50px] rounded-full text-xl ${'bg-blue-500 hover:bg-blue-600'} py-2 px-4`}
                onClick={() => {
                  submitForm({ mode: 'STRIPE' });
                }}
              >
                {'Pay with Card'}
              </button>
            )}
            {totalAmount > 0 && paymentModes?.vipps && (
              <button
                id="vippsButton"
                className={`text-white w-full h-[50px] rounded-full text-xl ${'bg-[#fe5b24] hover:bg-[#ee6f45]'} py-2 px-4`}
                onClick={() => {
                  submitForm({ mode: 'VIPPS' });
                }}
              >
                <div className="flex items-center justify-center">
                  Pay with{' '}
                  <span>
                    <img src={appSettings.images.VippsLogo} alt="Vipps" className="mt-2 ml-1" />
                  </span>
                </div>
              </button>
            )}
            {totalAmount > 0 && paymentModes?.dpo && authentication?.loggedIn && (
              <button
                onClick={() => {
                  submitForm({ mode: 'DPO' });
                }}
                className={`text-white w-full h-[50px] rounded-full text-xl ${'bg-blue-700 hover:bg-blue-800'} py-2 px-4`}
              >
                Pay with DPO
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Confirmation;
